// ────────────────────────────────────────────────────────────────────
// AssetWorkspaceScreen — Level 4: Asset detail
//
// Route: /assets/:assetId
// Shows:
//   - Breadcrumb (Atlas / Plant / Area / Asset)
//   - Asset header: tag · name · class · status
//   - Spec strip (design code, material, dims, t-min, drawing)
//   - 3D preview placeholder (STL via Three.js · Phase 4 wires real viewer)
//   - Inspection history timeline (horizontal · click point → /inspections/:id)
//   - Side panel: aggregate stats + technique counts
// ────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  ArrowLeft, Cylinder, Database, Factory, GitBranch,
  Calendar, FileText, User as UserIcon, Box, Circle, Download, Loader2, Info, ShieldCheck, Clock, Bot,
  ChevronLeft, ChevronRight, Maximize2, Minimize2, LayoutGrid,
} from "lucide-react";
import { paletteHex, type ColorMode } from "@/mockup3d/colormap";
import { hasMock3D } from "@/mockup3d/mockTags";
import { apiClient, getCachedUser, STAFF_ROLES, downloadCmlXlsx } from "@/lib/api";
import type { Asset, AssetClass, CmlRow, CorrosionData, AssetFinding, InspectionHistoryItem, AssetAssessment, EngineRecommendation } from "@/lib/types";
import { Gated, PremiumBadge } from "@/components/Gated";
import { useWorkspaceFocus } from "@/components/WorkspaceContext";
import { AssetStlViewer } from "@/components/AssetStlViewer";
import { AssetNavSidebar } from "@/components/AssetNavSidebar";
import { VerifyForm } from "@/components/VerifyForm";
import { RemainingLifeProjection } from "@/components/RemainingLifeProjection";
import { GuestInvitePanel } from "@/components/GuestInvitePanel";
import { AssetIntegrityView, type AssetFileRow } from "@/screens/asset/AssetIntegrityView";
import type { SidebarDue } from "@/screens/asset/AssetSidebar";
import { dqTooltip } from "@/lib/dqFlags";
import { useRecordRecentDesk } from "@/lib/recentDesks";
// Detailed per-asset 3D (project_epip vessel/tank scenes) — lazy so its R3F
// stack only loads when a matching vessel/tank asset is opened.
const AssetMockup3D = lazy(() => import("@/components/AssetMockup3D"));

// Per-asset health (worst_cml_status) → label + colour. "" = no t_min / no
// reading → shown as "No data", never green.
const ASSET_HEALTH: Record<string, { label: string; color: string }> = {
  crit:  { label: "Critical", color: "var(--err)" },
  warn:  { label: "Warn",     color: "var(--warn)" },
  watch: { label: "Watch",    color: "#CA8A04" },
  ok:    { label: "OK",       color: "var(--ok)" },
  "":    { label: "No data",  color: "#9CA3AF" },
};

const ASSET_CLASS_META: Record<AssetClass, { icon: typeof Database; color: string; label: string }> = {
  tank:           { icon: Database,  color: "#1F4E79", label: "Tank" },
  vessel:         { icon: Cylinder,  color: "#2E7BE0", label: "Vessel" },
  heat_exchanger: { icon: Factory,   color: "#D97706", label: "Heat Exchanger" },
  piping:         { icon: GitBranch, color: "#15803D", label: "Piping" },
  sphere:         { icon: Circle,    color: "#7C3AED", label: "Sphere" },
  cylo:           { icon: Cylinder,  color: "#0891B2", label: "Silo" },
  other:          { icon: Box,       color: "#5A6B82", label: "Other" },
};

// Portal asset tabs mirror the staff Review Studio's tab menu (same names + order)
// so an engineer can navigate both surfaces identically for QA/QC; the CONTENT of
// each tab stays curated to what the client needs (not the full back-office detail).
// ── The front page, and the seven tabs it did not replace ────────────
//
// `integrity` is the handoff's ASSET screen (design_handoff/README.md:152,
// "the centre of the system") and it is what /assets/:assetId renders FIRST.
//
// The other seven stay, per the product owner's ruling of 2026-08-31
// (docs/superpowers/specs/2026-08-31-staff-asset-screen-design.md §4): the
// handoff is a redesign of the INTEGRITY view, not a ruling that everything it
// omits should cease to exist. The three it names explicitly — the 3D surface
// (inside `cml`), the RBI register (`rbi`) and the photo gallery (inside
// `find`) — have real users and no replacement on the new front page, and the
// same argument covers the CML grid with its survey history and Excel export,
// the design spec, the filed documents, and the Assessment tab that carries
// `ReviewLayer`. Sign-off is load-bearing: `auth.require_signoff()` gates it
// server-side and an engineer was once wrongly 403'd from a report they were
// entitled to certify, so it was moved nowhere.
type AssetTab = "integrity" | "assess" | "rbi" | "cml" | "design" | "hist" | "find" | "docs";
const ASSET_TABS: readonly (readonly [AssetTab, string])[] = [
  ["integrity", "Integrity"],
  ["assess", "Assessment"], ["rbi", "RBI"], ["cml", "CML data"], ["design", "Design"],
  ["hist", "History"], ["find", "Findings"], ["docs", "Documents"],
];
type RbiSub = "register" | "corr" | "plan" | "risk";
const RBI_SUBS: readonly (readonly [RbiSub, string])[] = [
  ["register", "Asset register"], ["corr", "Corrosion manual"],
  ["plan", "Inspection plan"], ["risk", "Risk analysis"],
];

export default function AssetWorkspaceScreen(): JSX.Element {
  const { assetId } = useParams<{ assetId: string }>();
  const id = Number(assetId);
  useRecordRecentDesk(Number.isFinite(id) ? id : null);
  const navigate = useNavigate();
  // Desk entry (workbench roadmap C1) is a staff power tool — the calculator
  // and per-focus notes are an engineer's working surface, not something to
  // surface to a customer browsing their own asset.
  const cachedUser = getCachedUser();
  const isStaff = !!cachedUser && (STAFF_ROLES as readonly string[]).includes(cachedUser.role);

  // keepPreviousData on the per-asset queries: when switching assets from the
  // nav sidebar, the previous asset's data is retained while the new one loads,
  // so assetQ.isPending stays false and the layout (incl. the sidebar) is NOT
  // unmounted — the panel keeps its expand/scroll state across navigation.
  const assetQ = useQuery({
    queryKey: ["asset", id],
    queryFn: () => apiClient.asset(id),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
  const historyQ = useQuery({
    queryKey: ["inspection-history", id],
    queryFn: () => apiClient.assetInspectionHistory(id),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
  const corrosionQ = useQuery({
    queryKey: ["corrosion", id],
    queryFn: () => apiClient.assetCorrosion(id),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
  // Same query key as ExpertReviewPanel → React Query dedupes (no extra fetch);
  // surfaced as a compact credibility badge in the header so the client sees up
  // front whether this assessment is engineer-verified (the managed-service value).
  const reviewQ = useQuery({
    queryKey: ["review", id],
    queryFn: () => apiClient.assetReview(id),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
  // Sibling assets for the nav sidebar — same query key as the site map, so it
  // shares the cache (no extra fetch when arriving from the plant dashboard).
  // keepPreviousData holds the list steady through the brief window where the
  // new asset (hence its plant_id) is still loading.
  const plantId = assetQ.data?.plant_id;
  const plantAssetsQ = useQuery({
    queryKey: ["assets", { plant_id: plantId }],
    queryFn: () => apiClient.assets({ plant_id: plantId! }),
    enabled: !!plantId,
    placeholderData: keepPreviousData,
  });
  // ── the integrity front page's three sources ──
  // The record itself: every inspection date with its methods, findings and
  // files, the rate summary with the sentence behind each number, and every
  // interval/due candidate with its provenance. Nothing on that screen is
  // derived in the browser.
  const integrityQ = useQuery({
    queryKey: ["integrity", id],
    queryFn: () => apiClient.assetIntegrity(id),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
  // The sidebar's due lines. Every row but the open one is a DIFFERENT asset,
  // whose numbers are not on this asset's record — they are resolved
  // server-side by the compliance endpoint, over the same derived months the
  // integrity machinery produces. Same query key as CompliancePanel → React
  // Query dedupes, no extra fetch when arriving from the plant dashboard.
  const complianceQ = useQuery({
    queryKey: ["compliance", plantId],
    queryFn: () => apiClient.plantCompliance(plantId!),
    enabled: !!plantId,
    staleTime: 60_000,
  });
  // Names, kinds and download links for `InspectionEvent.file_ids`, which is a
  // list of ids and nothing else.
  const assetFilesQ = useQuery({
    queryKey: ["intake", { asset_id: id }],
    queryFn: () => apiClient.intakeList({ asset_id: id }),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
  const dueByAsset = useMemo(() => {
    const m = new Map<number, SidebarDue>();
    for (const r of complianceQ.data?.rows ?? []) {
      m.set(r.asset_id, { next_due: r.next_due, state: r.state, months_remaining: r.months_remaining });
    }
    return m;
  }, [complianceQ.data]);
  const assetFiles: AssetFileRow[] = useMemo(
    () => (assetFilesQ.data ?? []).map((f) => ({
      id: f.id, filename: f.filename, kind: f.kind, download_url: f.download_url,
    })),
    [assetFilesQ.data],
  );

  const asset = assetQ.data;
  const history = historyQ.data ?? [];
  const corrosion = corrosionQ.data;

  // Master-detail: the selected CML drives the trend/detail pane. Defaults to
  // the governing CML, else the worst-status one, else the first.
  const [selCmlId, setSelCmlId] = useState<number | null>(null);
  // Which survey the CML grid shows (null = latest). Lets you read past years.
  const [selSurvey, setSelSurvey] = useState<string | null>(null);
  // CML-tab layout balance between the 3D model and the data:
  //   data  → 3D collapsed to a rail, table + trend get the width
  //   split → 3D · table · trend share the row (default)
  //   focus → 3D dominant, trend panel hidden (the 3D is the visual; detail on demand)
  const [view3d, setView3d] = useState<"data" | "split" | "focus">("split");
  // 3D surface colouring — defaults to the remaining-life heat map on open, so the
  // model reads as a corrosion picture at a glance (toggle to corr-rate or status).
  const [colorMode, setColorMode] = useState<ColorMode>("rl");
  const [searchParams] = useSearchParams();
  const wantedTab = (): AssetTab => {
    const t = searchParams.get("tab") ?? "";
    // `integrity` used to be a legacy alias for the CML grid. It is now a real
    // tab key, and the ASSET_TABS check below matches it first, so an old
    // `?tab=integrity` link lands on the new integrity view rather than the CML
    // grid. That is the right destination for that word, and the alias is
    // dropped rather than left in place looking live.
    const legacy: Record<string, AssetTab> = { risk: "assess", history: "hist", cmls: "cml" };
    if (ASSET_TABS.some(([k]) => k === t)) return t as AssetTab;
    return legacy[t] ?? "integrity";                    // default = the handoff front page
  };
  const [tab, setTab] = useState<AssetTab>(wantedTab());
  const [rbiSub, setRbiSub] = useState<RbiSub>("register");
  useEffect(() => { setSelSurvey(null); setSelCmlId(null); setTab(wantedTab()); }, [id]);
  const selCml = useMemo(() => {
    if (!corrosion) return null;
    const explicit = corrosion.cmls.find((c) => c.cml_id === selCmlId);
    if (explicit) return explicit;
    const govNo = corrosion.rollup.governing?.cml_no;
    return (govNo ? corrosion.cmls.find((c) => c.cml_no === govNo) : undefined)
      ?? [...corrosion.cmls].sort((a, b) => STATUS_RANK[cmlStatus(b)] - STATUS_RANK[cmlStatus(a)])[0]
      ?? null;
  }, [corrosion, selCmlId]);

  const { setFocus } = useWorkspaceFocus();
  useEffect(() => {
    if (asset) {
      setFocus({ type: "asset", ref: String(asset.id), label: asset.tag });
    }
    return () => setFocus(null);   // clear focus when leaving the asset screen
  }, [asset, setFocus]);

  if (assetQ.isPending) return <CenterStatus>Loading asset…</CenterStatus>;
  if (assetQ.isError || !asset) {
    return (
      <CenterStatus tone="err">
        Asset not found.{" "}
        <Link to="/atlas" className="underline" style={{ color: "var(--accent-deep)" }}>Back to Atlas</Link>
      </CenterStatus>
    );
  }

  const meta = ASSET_CLASS_META[asset.asset_class] ?? ASSET_CLASS_META.other;
  const Icon = meta.icon;

  return (
    <div className="absolute inset-0 flex overflow-hidden" style={{ background: "var(--paper)" }}>

      {/* ── Asset nav sidebar — same component/chrome as the Site Map ──
          Not on the integrity tab: the handoff's screen is TWO columns and
          brings its own 254px sidebar (assets grouped by client, each row
          carrying its own due line). Two asset lists side by side would be
          two answers to the same question. */}
      {tab !== "integrity" && (
        <AssetNavSidebar
          assets={plantAssetsQ.data ?? []}
          plantId={plantId ?? undefined}
          activeAssetId={id}
          loading={plantAssetsQ.isPending}
          emptyHint="No sibling assets."
        />
      )}

      {/* ── Asset detail column ─────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

      {/* ── Breadcrumb ───────────────────────────────────────── */}
      <div className="px-6 h-10 border-b flex items-center justify-between shrink-0"
           style={{ borderColor: "var(--hairline)" }}>
        <div className="font-mono uppercase flex items-center gap-2"
             style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
          <Link to="/atlas" className="hover:text-[var(--accent)] transition-colors">ATLAS</Link>
          <span className="opacity-40">/</span>
          <Link to={asset.plant_id ? `/sites/${asset.plant_id}` : "/atlas"} className="hover:text-[var(--accent)]">
            {asset.plant_code || "—"}
          </Link>
          <span className="opacity-40">/</span>
          <span style={{ color: "var(--ink-1)" }}>{asset.tag}</span>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          {/* Guest access (workbench roadmap D2) — scoped + expiring
              third-party link to this asset. Staff only. */}
          {isStaff && <GuestInvitePanel scopeType="asset" scopeRef={asset.id} defaultLabel={asset.tag} />}
          {/* Desk entry (workbench roadmap C1) — the tiled work surface for
              this asset: CML table · selected CML trend · live what-if
              calculator, one shared focus. Staff only (see isStaff above). */}
          {isStaff && (
            <button onClick={() => navigate(`/desk/${asset.id}`)}
                    title="เปิดโต๊ะทำงานสำหรับ asset นี้"
                    className="font-mono uppercase flex items-center gap-1.5 px-2 py-1 hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors"
                    style={{ fontSize: 10.5, letterSpacing: ".16em", color: "var(--ink-3)", border: "1px solid var(--hairline)", cursor: "pointer" }}>
              <LayoutGrid size={11} />
              เปิดโต๊ะทำงาน
            </button>
          )}
          <Link to={asset.plant_id ? `/sites/${asset.plant_id}` : "/atlas"}
                className="font-mono uppercase flex items-center gap-1.5 hover:text-[var(--accent)] transition-colors"
                style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
            <ArrowLeft size={11} />
            BACK TO {asset.plant_code || "PLANT"}
          </Link>
        </div>
      </div>

      {/* ── Asset header ─────────────────────────────────────── */}
      <header className="px-8 py-5 border-b" style={{ borderColor: "var(--hairline)" }}>
        <div className="flex items-start justify-between gap-8">
          <div className="flex items-start gap-4 min-w-0">
            {/* Class icon badge */}
            <div className="w-14 h-14 flex items-center justify-center shrink-0"
                 style={{ background: `${meta.color}10`, border: `1.5px solid ${meta.color}40` }}>
              <Icon size={26} style={{ color: meta.color }} />
            </div>
            <div className="min-w-0">
              <div className="font-mono uppercase mb-1"
                   style={{ fontSize: 10.5, letterSpacing: ".24em", color: "var(--accent-deep)", fontWeight: 600 }}>
                / {meta.label.toUpperCase()} · {asset.client_name}
              </div>
              {/* tag + disposition badge on one line */}
              <div className="flex items-center gap-3">
                <span className="font-mono whitespace-nowrap"
                      style={{ fontSize: 24, letterSpacing: ".06em", color: "var(--accent-deep)", fontWeight: 700 }}>
                  {asset.tag}
                </span>
                {(() => { const v = assetVerdict(corrosion?.rollup); return v ? (
                  <span className="font-mono uppercase shrink-0 px-2 py-0.5"
                        style={{ fontSize: 9.5, letterSpacing: ".1em", fontWeight: 700, color: "#fff", background: v.color }}>{v.label}</span>
                ) : null; })()}
              </div>
              {/* name on its own line — only when it adds something */}
              {asset.name && asset.name !== asset.tag && (
                <h1 className="font-sans font-medium m-0 mt-1"
                    style={{ fontSize: 28, color: "var(--ink-1)", letterSpacing: "-.015em", lineHeight: 1.1 }}>
                  {asset.name}
                </h1>
              )}
              {/* Credibility line — is this assessment engineer-verified? (the managed-
                  service signal). Same data as the Risk & Recommendation review panel. */}
              {(() => {
                const rv = reviewQ.data;
                const verified = rv?.status === "verified";
                const pending = rv?.status === "requested" || rv?.status === "in_review";
                const fd = (s?: string) => (s ? new Date(s).toLocaleDateString() : "");
                return (
                  <div className="flex items-center gap-1.5 mt-2 font-mono" style={{ fontSize: 10.5, letterSpacing: ".03em" }}>
                    {verified ? <ShieldCheck size={13} style={{ color: "var(--ok)" }} />
                      : pending ? <Clock size={13} style={{ color: "var(--warn)" }} />
                      : <Bot size={13} style={{ color: "var(--ink-4)" }} />}
                    {verified ? (
                      <span style={{ color: "var(--ok)" }}>
                        Engineer-verified{rv?.reviewer_name ? ` · ${rv.reviewer_name}` : ""}{rv?.reviewer_cert ? ` · ${rv.reviewer_cert}` : ""}{rv?.reviewed_at ? ` · ${fd(rv.reviewed_at)}` : ""}
                        {rv?.stale && <span style={{ color: "var(--warn)" }}> · inputs changed — re-review</span>}
                      </span>
                    ) : pending ? (
                      <span style={{ color: "var(--warn)" }}>{rv?.status === "in_review" ? `Under review by ${rv?.reviewer_name || "an engineer"}` : "Engineer sign-off requested"}</span>
                    ) : (
                      <span style={{ color: "var(--ink-3)" }}>Preliminary — engine analysis, not engineer-verified</span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* KPI strip — NOT on the integrity tab.
              Its NEXT DUE comes from the corrosion rollup, which applies a
              hardcoded 10-year cap; the front page's rate strip publishes the
              same question as ordered candidates, each with the sentence it
              came from. Two next-due dates side by side, one of them with no
              provenance, is precisely the failure the 2026-08-27 ruling exists
              to prevent — a hardcoded number winning silently. The tabs that
              still read the rollup keep the strip. */}
          {tab !== "integrity" && (
          <div className="grid grid-cols-4 shrink-0"
               style={{ border: "1px solid var(--hairline)", background: "var(--paper-2)" }}>
            <KpiCell label="STATUS" value={ASSET_HEALTH[asset.worst_cml_status]?.label ?? "—"}
                     valueColor={ASSET_HEALTH[asset.worst_cml_status]?.color}
                     tip="Worst CML — thickness now vs minimum allowable (t_min). Critical ≤ t_min · Warn within 20% · OK above · No data = t_min/reading unknown. Live from the latest reading." />
            <KpiCell label="CMLs" value={asset.cml_count}
                     tip="Condition Monitoring Locations — the fixed measurement points tracked on this asset." />
            <KpiCell label="INSPECTIONS" value={asset.inspection_count}
                     tip="Inspection records on file for this asset (UTM surveys, GVI walkdowns…)." />
            <KpiCell label="NEXT DUE"
                     value={corrosion?.rollup.overdue ? "OVERDUE" : (corrosion?.rollup.next_inspection_due ?? "—")}
                     valueColor={corrosion?.rollup.overdue ? "var(--err)" : undefined}
                     tip={corrosion?.rollup.interval_basis
                          ? `Next inspection due — ${corrosion.rollup.interval_basis} (API 510/570 half-life rule).`
                          : "Next inspection due = last survey + min(½ × remaining life, code max 10 yr). Shown only when remaining life is computable."} />
          </div>
          )}
        </div>
      </header>

      {/* ── Tabs ─────────────────────────────────────────────── */}
      <div className="px-6 flex items-center gap-1 border-b overflow-x-auto" style={{ borderColor: "var(--hairline)" }}>
        {ASSET_TABS.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
                  className="px-3.5 py-3 font-mono uppercase transition-colors whitespace-nowrap"
                  style={{ fontSize: 10.5, letterSpacing: ".16em",
                           color: tab === key ? "var(--ink-1)" : "var(--ink-3)",
                           borderBottom: tab === key ? "2px solid var(--accent)" : "2px solid transparent" }}>
            {label}{key === "hist" ? ` · ${asset.inspection_count}` : ""}
          </button>
        ))}

        {/* Jump back to the plant plot plan in PID or 3D */}
        {asset.plant_id && (
          <div className="ml-auto flex items-center gap-1 font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".12em" }}>
            {(["2d", "3d"] as const).map((v) => (
              <Link key={v} to={`/sites/${asset.plant_id}?view=${v}`}
                    className="px-2 py-0.5 border transition-colors hover:text-[var(--accent)] hover:border-[var(--accent)]"
                    style={{ borderColor: "var(--hairline)", color: "var(--ink-3)" }}>
                {v === "2d" ? "PID" : "3D PLOT PLAN"}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* ── Tab content ──────────────────────────────────────── */}
      {/* The integrity view scrolls its own detail column (the sidebar scrolls
          separately, as the handoff draws it), so it is mounted OUTSIDE the
          shared overflow-auto box the seven older tabs share. */}
      {tab === "integrity" && (
        integrityQ.isPending ? (
          <div className="flex-1 min-h-0 p-6 font-mono text-xs" style={{ color: "var(--ink-3)" }}>
            Loading the integrity record…
          </div>
        ) : integrityQ.isError || !integrityQ.data ? (
          <div className="flex-1 min-h-0 p-6 font-mono text-xs" style={{ color: "var(--err)" }}>
            Could not load this asset's integrity record. Nothing on this screen is computed in the
            browser, so there is nothing to show without it.
          </div>
        ) : (
          <AssetIntegrityView
            record={integrityQ.data}
            asset={asset}
            sidebarAssets={plantAssetsQ.data ?? []}
            dueByAsset={dueByAsset}
            sidebarLoading={plantAssetsQ.isPending}
            onSelectAsset={(next) => navigate(`/assets/${next}`)}
            files={assetFiles}
          />
        )
      )}

      <div className="flex-1 overflow-auto min-h-0" hidden={tab === "integrity"}>

        {tab === "cml" && (
          <div className="pb-6">
            {/* CML integrity — grid + 3D split (survey dropdown reads back-years) */}
            <div className="px-6 pt-5 pb-2">
              <div className="flex items-center justify-between mb-3 gap-3">
                <div className="font-mono uppercase" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
                  / CML INTEGRITY
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {corrosion && corrosion.surveys.length > 1 && (() => {
                    const latest = corrosion.surveys[corrosion.surveys.length - 1];
                    return (
                      <label className="flex items-center gap-2 font-mono uppercase shrink-0" style={{ fontSize: 9, letterSpacing: ".14em", color: "var(--ink-4)" }}>
                        Survey
                        <select value={selSurvey ?? latest}
                                onChange={(e) => setSelSurvey(e.target.value === latest ? null : e.target.value)}
                                className="font-mono tabular-nums px-2 py-1"
                                style={{ fontSize: 10.5, letterSpacing: ".02em", color: "var(--ink-1)", background: "var(--paper)", border: "1px solid var(--hairline)", cursor: "pointer" }}>
                          {[...corrosion.surveys].reverse().map((d, i) => (
                            <option key={d} value={d}>{d.slice(0, 10)}{i === 0 ? "  · latest" : ""}</option>
                          ))}
                        </select>
                      </label>
                    );
                  })()}
                  <button onClick={() => { void downloadCmlXlsx(asset.id); }}
                          title="Download this asset's CML table as an Excel workbook"
                          aria-label="Export CML table to Excel"
                          className="flex items-center gap-1.5 font-mono uppercase px-2 py-1 hover:text-[var(--accent)] shrink-0"
                          style={{ fontSize: 9, letterSpacing: ".14em", color: "var(--ink-3)", background: "var(--paper)", border: "1px solid var(--hairline)", cursor: "pointer" }}>
                    <Download size={11} />
                    Excel
                  </button>
                </div>
              </div>
              {corrosionQ.isPending && <div className="font-mono text-xs" style={{ color: "var(--ink-3)" }}>Computing…</div>}
              {corrosionQ.isError && <div className="font-mono text-xs" style={{ color: "var(--err)" }}>Could not load corrosion analysis.</div>}
              {corrosion && (
                // 3D · CML table · trend — three density modes (view3d):
                //   data: 3D collapsed · split: shared · focus: 3D dominant, trend hidden
                <div className="grid gap-4 items-stretch" style={{ gridTemplateColumns:
                    view3d === "data"  ? "auto minmax(440px,1.6fr) minmax(320px,1fr)"
                  : view3d === "focus" ? "minmax(520px,2.2fr) minmax(300px,1fr)"
                  :                      "minmax(300px,1.1fr) minmax(440px,1.3fr) minmax(320px,1fr)" }}>
                  {view3d !== "data" ? (
                    <div className="flex flex-col border" style={{ borderColor: "var(--hairline)" }}>
                      <div className="flex items-center justify-between px-2.5 py-1.5 border-b shrink-0"
                           style={{ borderColor: "var(--hairline)" }}>
                        <span className="font-mono uppercase flex items-center gap-1.5"
                              style={{ fontSize: 9, letterSpacing: ".14em", color: "var(--ink-3)" }}>
                          3D Model <PremiumBadge light />
                        </span>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setView3d(view3d === "focus" ? "split" : "focus")}
                                  title={view3d === "focus" ? "Back to split view" : "Focus the 3D model (hide trend)"}
                                  aria-label={view3d === "focus" ? "Back to split view" : "Focus the 3D model"}
                                  className="p-0.5 hover:text-[var(--accent)]" style={{ color: "var(--ink-3)" }}>
                            {view3d === "focus" ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                          </button>
                          <button onClick={() => setView3d("data")} title="Collapse 3D model"
                                  aria-label="Collapse 3D model"
                                  className="p-0.5 hover:text-[var(--accent)]" style={{ color: "var(--ink-3)" }}>
                            <ChevronLeft size={15} />
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 min-h-0">
                        <Gated feature="model_3d" title="3D model"
                               blurb="View the CAD / laser-scan geometry with the CML plan mapped onto the asset.">
                          {asset.has_stl || hasMock3D(asset.tag) ? (
                            <Suspense fallback={<ThreeDPlaceholder />}>
                              {/* AssetMockup3D renders the bundled JSM-A scene for
                                  mock-matched tags; otherwise falls back to the
                                  children (the STL viewer — only valid with an STL). */}
                              <AssetMockup3D asset={asset} cmls={corrosion.cmls} colorMode={colorMode}>
                                {asset.has_stl ? <AssetStlViewer asset={asset} /> : <ThreeDPlaceholder />}
                              </AssetMockup3D>
                            </Suspense>
                          ) : <ThreeDPlaceholder />}
                        </Gated>
                      </div>
                      {/* Heat-map mode toggle + legend — surface coloured by the real register */}
                      <div className="flex items-center gap-2 px-2.5 py-1.5 border-t shrink-0" style={{ borderColor: "var(--hairline)" }}>
                        <div className="flex" style={{ border: "1px solid var(--hairline)" }}>
                          {([["rl", "Rem. life"], ["cr", "Corr. rate"], ["status", "Status"]] as [ColorMode, string][]).map(([m, lbl]) => (
                            <button key={m} onClick={() => setColorMode(m)} title={`Colour the model by ${lbl}`}
                                    className="font-mono uppercase px-2 py-0.5 transition-colors"
                                    style={{ fontSize: 8.5, letterSpacing: ".1em",
                                      background: colorMode === m ? "var(--accent)" : "transparent",
                                      color: colorMode === m ? "#fff" : "var(--ink-3)" }}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                        {colorMode !== "status" && (
                          <div className="flex items-center gap-1.5 ml-auto">
                            <span className="font-mono uppercase" style={{ fontSize: 7.5, letterSpacing: ".08em", color: "var(--ink-4)" }}>{colorMode === "rl" ? "long" : "low"}</span>
                            <div style={{ width: 88, height: 7, background: `linear-gradient(to right, ${paletteHex(0)}, ${paletteHex(0.3)}, ${paletteHex(0.55)}, ${paletteHex(0.78)}, ${paletteHex(1)})` }} />
                            <span className="font-mono uppercase" style={{ fontSize: 7.5, letterSpacing: ".08em", color: "var(--ink-4)" }}>{colorMode === "rl" ? "short" : "high"}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setView3d("split")} title="Show 3D model"
                            aria-label="Show 3D model"
                            className="h-full flex flex-col items-center justify-center gap-2 border transition-colors hover:bg-[var(--paper)]"
                            style={{ borderColor: "var(--hairline)", background: "var(--paper-2)", minWidth: 36 }}>
                      <ChevronRight size={14} style={{ color: "var(--accent)" }} />
                      <span className="font-mono uppercase"
                            style={{ writingMode: "vertical-rl", fontSize: 9, letterSpacing: ".16em", color: "var(--ink-3)" }}>
                        3D Model
                      </span>
                    </button>
                  )}
                  <CmlIntegrityTable cmls={corrosion.cmls} govCml={corrosion.rollup.governing?.cml_no}
                                     selId={selCml?.cml_id} onSelect={(c) => setSelCmlId(c.cml_id)} survey={selSurvey} />
                  {view3d !== "focus" && (
                  <div className="border" style={{ borderColor: "var(--hairline)" }}>
                    {selCml ? <CmlDetail c={selCml} /> : (
                      <div className="h-full flex items-center justify-center p-6 text-xs font-mono text-center" style={{ color: "var(--ink-3)" }}>Select a CML to see its thickness trend.</div>
                    )}
                  </div>
                  )}
                </div>
              )}
            </div>

          </div>
        )}

        {tab === "find" && (
          <div className="pb-6">
            {/* GVI findings + photos */}
            <div className="px-6 pt-5 pb-2"><FindingsPanel assetId={id} /></div>
            <div className="px-6 pt-2 pb-2"><PhotosPanel assetId={id} /></div>
          </div>
        )}

        {tab === "design" && (
          <div className="pb-6 pt-5">
            {/* Design spec — only when on file */}
            {(() => {
              const hasSpec = !!(asset.design_code || asset.material || asset.service || asset.drawing_no
                || (asset.diameter_mm ?? 0) > 0 || (asset.height_mm ?? 0) > 0
                || (asset.t_nominal_mm ?? 0) > 0 || (asset.t_min_design_mm ?? 0) > 0
                || asset.design_temp_c != null || asset.design_pressure_mpa != null || asset.mawp_mpa != null);
              if (!hasSpec) return null;
              return (
                <div className="px-6 pt-2 pb-2">
                  <div className="font-mono uppercase mb-3" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>/ DESIGN SPEC</div>
                  <div className="grid grid-cols-4 border" style={{ borderColor: "var(--hairline)" }}>
                    <SpecCell label="DESIGN CODE" value={asset.design_code || "—"} />
                    <SpecCell label="MATERIAL"    value={asset.material || "—"} />
                    <SpecCell label="SERVICE"     value={asset.service || "—"} />
                    <SpecCell label="DRAWING NO"  value={asset.drawing_no || "—"} />
                    <SpecCell label="DIAMETER"    value={asset.diameter_mm ? `${asset.diameter_mm} mm` : "—"} />
                    <SpecCell label="HEIGHT"      value={asset.height_mm   ? `${asset.height_mm} mm`   : "—"} />
                    <SpecCell label="t-NOMINAL"   value={asset.t_nominal_mm    ? `${asset.t_nominal_mm} mm` : "—"} />
                    <SpecCell label="t-MIN DESIGN" value={asset.t_min_design_mm ? `${asset.t_min_design_mm} mm` : "—"} />
                    <SpecCell label="DESIGN TEMP" value={asset.design_temp_c != null ? `${asset.design_temp_c} °C` : "—"} />
                    <SpecCell label="DESIGN PRESS" value={asset.design_pressure_mpa != null ? `${asset.design_pressure_mpa} MPa` : "—"} />
                    <SpecCell label="MAWP"        value={asset.mawp_mpa != null ? `${asset.mawp_mpa} MPa` : "—"} />
                    <SpecCell label="CORR. ALLOW" value={asset.corrosion_allowance_mm != null ? `${asset.corrosion_allowance_mm} mm` : "—"} />
                  </div>
                </div>
              );
            })()}

            {/* Damage mechanism + corrosion loop (RBI · API 571) — from the RBI register */}
            {(asset.damage_mechanism || asset.corrosion_loop) && (
              <div className="px-6 pt-1 pb-3">
                <div className="font-mono uppercase mb-2 flex items-center gap-2"
                     style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
                  / DAMAGE MECHANISM · API 571
                  {asset.corrosion_loop && (
                    <span style={{ color: "var(--ink-3)", letterSpacing: ".08em" }}>
                      · LOOP <span style={{ color: "var(--accent-deep)" }}>{asset.corrosion_loop}</span>
                    </span>
                  )}
                </div>
                {asset.damage_mechanism ? (
                  <div className="flex flex-wrap gap-1.5">
                    {asset.damage_mechanism.split(",").map((s) => s.trim()).filter(Boolean).map((dm, i) => (
                      <span key={i} className="font-mono"
                            style={{ fontSize: 11, padding: "3px 9px", borderRadius: 2,
                                     border: "1px solid var(--hairline)", background: "var(--paper-2)", color: "var(--ink-2)" }}>
                        {dm}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
                    No damage mechanism assigned.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "hist" && (
          <div className="px-6 pt-5 pb-6">
            <div className="font-mono uppercase mb-3" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
              / INSPECTION HISTORY · {history.length}
            </div>
            {historyQ.isPending && <div className="p-6 text-sm font-mono text-center" style={{ color: "var(--ink-3)" }}>Loading…</div>}
            {historyQ.isSuccess && history.length === 0 && (
              <div className="p-6 text-sm font-mono text-center" style={{ color: "var(--ink-3)" }}>No inspections on record for this asset.</div>
            )}
            {history.length > 0 && (
              <div className="border divide-y" style={{ borderColor: "var(--hairline)" }}>
                {history.map((it) => <HistoryRow key={it.id} it={it} />)}
              </div>
            )}
          </div>
        )}

        {tab === "assess" && (
          <div className="px-6 pt-5 pb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-mono uppercase" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>/ ASSESSMENT</span>
              <PremiumBadge />
            </div>
            <Gated feature="recommendation" title="Assessment"
                   blurb="The engineer-signed disposition (continue / monitor / repair / FFS), remaining-life projection and action plan for this asset.">
              <RecommendationPanel assetId={id} corrosion={corrosion} />
            </Gated>
          </div>
        )}

        {tab === "rbi" && (
          <div className="px-6 pt-5 pb-6">
            <RbiTab assetId={id} asset={asset} corrosion={corrosion} sub={rbiSub} setSub={setRbiSub} />
          </div>
        )}

        {tab === "docs" && (
          <div className="px-6 pt-5 pb-6">
            <DocumentsTab history={history} asset={asset} />
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

// ── RBI tab — sub-tabs mirror the studio's RBI sheets (Asset register / Corrosion
// manual / Inspection plan / Risk analysis), curated to what the client needs.
// Asset + corrosion data drive it now; a full API 581 PoF×CoF rating renders here
// when an RBI study has been imported for the asset's tenant. ──
const RISK_COLOR: Record<string, string> = {
  low: "#2F7D4F", "medium-low": "#7A9A3F", medium: "#D97706",
  "medium-high": "#C0392B", high: "#C0392B",
};
const capRisk = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, " ") : "—");

function RbiTab({ assetId, asset, corrosion, sub, setSub }:
  { assetId: number; asset: Asset; corrosion?: CorrosionData; sub: RbiSub; setSub: (s: RbiSub) => void }): JSX.Element {
  // Same source the staff Review Studio's RBI tab uses → the two surfaces relate.
  const rbiQ = useQuery({ queryKey: ["rbi", assetId], queryFn: () => apiClient.assetRbi(assetId), enabled: !!assetId });
  const rbi = rbiQ.data;
  const r = corrosion?.rollup;
  const dms = (asset.damage_mechanism || "").split(",").map((s) => s.trim()).filter(Boolean);
  const note = (t: string) => <p className="font-mono mt-2" style={{ fontSize: 10, color: "var(--ink-4)", letterSpacing: ".03em", lineHeight: 1.5 }}>{t}</p>;
  const tile = (label: string, value: string, color?: string) => (
    <div className="px-3 py-2 border-r last:border-r-0" style={{ borderColor: "var(--hairline)" }}>
      <div className="font-mono uppercase" style={{ fontSize: 8, letterSpacing: ".14em", color: "var(--ink-4)" }}>{label}</div>
      <div className="font-mono tabular-nums truncate" style={{ fontSize: 13, fontWeight: 700, color: color ?? "var(--ink-1)" }}>{value}</div>
    </div>
  );
  return (
    <div>
      <div className="flex items-center gap-1 mb-4 border-b overflow-x-auto" style={{ borderColor: "var(--hairline)" }}>
        {RBI_SUBS.map(([k, label]) => (
          <button key={k} onClick={() => setSub(k)}
                  className="px-3 py-2 font-mono uppercase whitespace-nowrap transition-colors"
                  style={{ fontSize: 9.5, letterSpacing: ".12em",
                           color: sub === k ? "var(--ink-1)" : "var(--ink-3)",
                           borderBottom: sub === k ? "2px solid var(--accent)" : "2px solid transparent" }}>
            {label}
          </button>
        ))}
      </div>

      {sub === "register" && (
        rbi && rbi.components.length > 0 ? (
          <div className="border" style={{ borderColor: "var(--hairline)" }}>
            <div className="grid items-center gap-2 px-3 py-2 border-b font-mono uppercase"
                 style={{ gridTemplateColumns: "1.6fr 1fr 1fr 1.2fr 0.8fr", fontSize: 8.5, letterSpacing: ".1em", color: "var(--ink-4)", background: "var(--paper-2)", borderColor: "var(--hairline)" }}>
              <span>Component</span><span>Type</span><span>Loop</span><span>Material</span><span className="text-right">PoF cat</span>
            </div>
            {rbi.components.map((c) => (
              <div key={c.id} className="grid items-center gap-2 px-3 py-1.5 border-b font-mono"
                   style={{ gridTemplateColumns: "1.6fr 1fr 1fr 1.2fr 0.8fr", fontSize: 11, borderColor: "var(--hairline)", color: "var(--ink-2)" }}>
                <span className="truncate" style={{ color: "var(--ink-1)" }}>{c.label || "—"}</span>
                <span className="truncate">{c.component_type || "—"}</span>
                <span className="truncate">{c.corrosion_loop || "—"}</span>
                <span className="truncate">{c.material_spec || "—"}</span>
                <span className="text-right tabular-nums">{c.rbi_pof_category ?? "—"}</span>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-4 border" style={{ borderColor: "var(--hairline)" }}>
              <SpecCell label="MATERIAL" value={asset.material || "—"} />
              <SpecCell label="SERVICE" value={asset.service || "—"} />
              <SpecCell label="DESIGN CODE" value={asset.design_code || "—"} />
              <SpecCell label="CORROSION LOOP" value={asset.corrosion_loop || "—"} />
              <SpecCell label="t-NOMINAL" value={asset.t_nominal_mm ? `${asset.t_nominal_mm} mm` : "—"} />
              <SpecCell label="t-MIN DESIGN" value={asset.t_min_design_mm ? `${asset.t_min_design_mm} mm` : "—"} />
              <SpecCell label="CORR. ALLOW" value={asset.corrosion_allowance_mm != null ? `${asset.corrosion_allowance_mm} mm` : "—"} />
              <SpecCell label="DESIGN PRESS" value={asset.design_pressure_mpa != null ? `${asset.design_pressure_mpa} MPa` : "—"} />
            </div>
            {note("Per-component RBI register appears here once an API 581 study is imported; otherwise the asset-level design basis is shown.")}
          </div>
        )
      )}

      {sub === "corr" && (
        rbi && (rbi.loops.length > 0 || rbi.study_loops.length > 0) ? (
          <div className="border divide-y" style={{ borderColor: "var(--hairline)" }}>
            {(rbi.loops.length ? rbi.loops : rbi.study_loops).slice(0, 40).map((lp, i) => (
              <div key={i} className="px-3 py-2 font-mono" style={{ fontSize: 11, color: "var(--ink-2)" }}>
                <span style={{ color: "var(--accent-deep)", fontWeight: 700 }}>{lp.loop_no || "—"}</span>{lp.loop_name ? ` · ${lp.loop_name}` : ""}
                {lp.loop_description && <div style={{ fontSize: 10, color: "var(--ink-4)", marginTop: 2, lineHeight: 1.4 }}>{lp.loop_description}</div>}
              </div>
            ))}
          </div>
        ) : (
          <div>
            {dms.length ? (
              <div className="flex flex-wrap gap-1.5">
                {dms.map((dm, i) => (
                  <span key={i} className="font-mono" style={{ fontSize: 11, padding: "3px 9px", borderRadius: 2, border: "1px solid var(--hairline)", background: "var(--paper-2)", color: "var(--ink-2)" }}>{dm}</span>
                ))}
              </div>
            ) : note("No corrosion loop / damage mechanism on record for this asset.")}
            {note(`Damage mechanisms per API 571${asset.corrosion_loop ? ` · loop ${asset.corrosion_loop}` : ""}. Fluid composition and loop conditions show when the corrosion study is imported.`)}
          </div>
        )
      )}

      {sub === "plan" && (
        <div>
          <div className="grid grid-cols-3 border" style={{ borderColor: "var(--hairline)" }}>
            <SpecCell label="NEXT DUE" value={r?.overdue ? "OVERDUE" : (r?.next_inspection_due ?? "—")} />
            <SpecCell label="INTERVAL" value={r?.next_interval_years != null ? `${r.next_interval_years} yr` : "—"} />
            <SpecCell label="GOVERNING CML" value={r?.governing?.cml_no ?? "—"} />
          </div>
          {r?.interval_basis && note(r.interval_basis)}
          {note(rbi?.inspection_coverage.has_plan
            ? `Risk-based ITP on file — ${rbi.inspection_coverage.components.length} component(s) planned${rbi.inspection_coverage.n_gaps ? `, ${rbi.inspection_coverage.n_gaps} coverage gap(s)` : ""}.`
            : "Interval is computed from the measured corrosion rate (API 510/570); a full risk-based ITP shows when an RBI study is imported.")}
        </div>
      )}

      {sub === "risk" && (
        rbi ? (
          <div>
            <div className="grid grid-cols-4 border" style={{ borderColor: "var(--hairline)" }}>
              {tile("RBI RISK", capRisk(rbi.risk.risk_level), RISK_COLOR[rbi.risk.risk_level])}
              {tile("PoF CATEGORY", rbi.risk.pof_category != null ? String(rbi.risk.pof_category) : "—")}
              {tile("CoF CATEGORY", rbi.risk.cof_category ?? "—")}
              {tile("GOVERNING CML", rbi.pof.governing_cml ?? "—")}
            </div>
            <div className="grid grid-cols-4 border" style={{ borderColor: "var(--hairline)", borderTop: "none" }}>
              {tile("PoF /yr", rbi.pof.pof_per_year != null ? rbi.pof.pof_per_year.toExponential(1) : "—")}
              {tile("DAMAGE FACTOR", rbi.pof.df_total != null ? rbi.pof.df_total.toFixed(1) : "—")}
              {tile("CoF AREA", rbi.cof.cof_area_m2 != null ? `${Math.round(rbi.cof.cof_area_m2)} m²` : "—")}
              {tile("DATA", rbi.risk.data_quality)}
            </div>
            {note(rbi.validation_caveat || "API 580/581 risk = PoF × CoF with representative bins — validate against your organization's risk matrix before ranking decisions.")}
          </div>
        ) : rbiQ.isPending ? note("Computing RBI…") : note("No RBI data available for this asset.")
      )}
    </div>
  );
}

// ── Documents tab — uploaded files filed to this asset + inspection reports. ──
function DocumentsTab({ history, asset }: { history: InspectionHistoryItem[]; asset: Asset }): JSX.Element {
  return (
    <div className="flex flex-col gap-7">
      <FiledDocuments asset={asset} />
      <InspectionReports history={history} asset={asset} />
    </div>
  );
}

// Files a customer uploaded and staff routed here (the destination of intake).
function FiledDocuments({ asset }: { asset: Asset }): JSX.Element {
  const docsQ = useQuery({
    queryKey: ["intake", "asset", asset.id],
    queryFn: () => apiClient.intakeList({ asset_id: asset.id }),
    staleTime: 30_000,
  });
  const docs = docsQ.data ?? [];
  return (
    <div>
      <div className="font-mono uppercase mb-3" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
        / UPLOADED DOCUMENTS · {docs.length}
      </div>
      {docs.length === 0 ? (
        <div className="border p-6 text-center font-mono" style={{ fontSize: 11, color: "var(--ink-3)", borderColor: "var(--hairline)" }}>
          No documents filed to {asset.tag} yet — upload on the Upload page and the team will file them here.
        </div>
      ) : (
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))" }}>
          {docs.map((d) => {
            const isPhoto = d.kind === "photo" && !!d.download_url;
            const body = (
              <div className="flex items-center gap-2.5 px-3 py-2.5 h-full"
                   style={{ border: "1px solid var(--hairline)", borderRadius: 9, background: "var(--paper-2)" }}>
                <div className="shrink-0 flex items-center justify-center overflow-hidden"
                     style={{ width: 38, height: 38, borderRadius: 7, background: "var(--paper)", border: "1px solid var(--hairline)" }}>
                  {isPhoto ? <img src={d.download_url!} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                           : <FileText size={16} style={{ color: "var(--ink-3)" }} />}
                </div>
                <div className="min-w-0">
                  <div className="font-mono truncate" style={{ fontSize: 12, color: "var(--ink-1)", fontWeight: 600 }}>{d.filename}</div>
                  <div className="font-mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
                    <span className="uppercase tracking-wider">{d.kind}</span>{d.note ? ` · ${d.note}` : ""}
                  </div>
                </div>
              </div>
            );
            return d.download_url
              ? <a key={d.id} href={d.download_url} target="_blank" rel="noreferrer" className="block hover:opacity-90 transition-opacity">{body}</a>
              : <div key={d.id}>{body}</div>;
          })}
        </div>
      )}
    </div>
  );
}

// Inspection reports on record (the curated, parsed deliverables).
function InspectionReports({ history, asset }: { history: InspectionHistoryItem[]; asset: Asset }): JSX.Element {
  return (
    <div>
      <div className="font-mono uppercase mb-3" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>/ INSPECTION REPORTS · {history.length}</div>
      {history.length === 0 ? (
        <div className="border p-6 text-center font-mono" style={{ fontSize: 11, color: "var(--ink-3)", borderColor: "var(--hairline)" }}>
          No inspection reports on record for {asset.tag}.
        </div>
      ) : (
        <div className="border divide-y" style={{ borderColor: "var(--hairline)" }}>
          {history.map((it) => (
            <Link key={it.id} to={`/inspections/${it.id}`}
                  className="px-4 py-3 flex items-center justify-between transition-colors hover:bg-[var(--paper)]">
              <span className="font-mono" style={{ fontSize: 12, color: "var(--ink-1)" }}>
                {it.technique || "Inspection"} · {String(it.inspection_date).slice(0, 10)}{it.inspector_name ? ` · ${it.inspector_name}` : ""}
              </span>
              <span className="font-mono flex items-center gap-1.5" style={{ fontSize: 10, letterSpacing: ".06em", color: "var(--accent-deep)" }}>
                {it.has_raw_excel && <Download size={12} />}{it.has_raw_excel ? "RAW DATA" : "OPEN"} →
              </span>
            </Link>
          ))}
        </div>
      )}
      <p className="font-mono mt-3" style={{ fontSize: 9.5, color: "var(--ink-4)", letterSpacing: ".03em" }}>
        Open an inspection to view its readings and download the original source report.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CML integrity — governing bar + table (the heart of the workspace)
// ─────────────────────────────────────────────────────────────────────
const fmtMm = (n: number | null): string => (n != null ? n.toFixed(2) : "—");
const fmtRate = (n: number | null): string => (n != null ? n.toFixed(3) : "—");
const fmtYr = (n: number | null): string => (n == null ? "—" : n >= 100 ? ">100" : n.toFixed(1));

// Live per-CML status from latest reading vs t_min. "" = spec/reading unknown.
function cmlStatus(c: CmlRow): "crit" | "warn" | "ok" | "" {
  if (!c.t_min_mm || c.t_min_mm <= 0 || c.t_actual_mm == null || c.t_actual_mm <= 0) return "";
  if (c.t_actual_mm <= c.t_min_mm) return "crit";
  if (c.t_actual_mm < c.t_min_mm * 1.20) return "warn";
  return "ok";
}
const STATUS_RANK: Record<string, number> = { crit: 3, warn: 2, ok: 1, "": 0 };
// Client integrity table: dot+CML · MIN (governing reading) · t-MIN · MARGIN · CR · RL.
// The 4 clock-position readings (12/3/6/9 o'clock) are the inspector/engineer's
// working detail — they live in the staff Review Studio, not this client view. The
// numbers here all come from the SAME /corrosion engine the studio uses (no drift).
const GRID = "minmax(96px,1fr) 56px 56px 66px 64px 60px";


// Headline verdict from the (free) corrosion rollup — the answer-first chip that
// stays visible above the tabs. Insufficient data ⇒ no verdict (honest).
function assetVerdict(rollup: CorrosionData["rollup"] | undefined): { label: string; color: string } | null {
  if (!rollup) return null;
  if (rollup.n_below_tmin > 0) return { label: "FFS / REPAIR", color: "var(--err)" };
  if (rollup.overdue) return { label: "INSPECT NOW", color: "var(--err)" };
  const rl = rollup.min_remaining_life_years;
  if (rl != null && rl < 5) return { label: "MONITOR · SHORT INTERVAL", color: "var(--warn)" };
  if (rl != null || rollup.no_measurable_loss) return { label: "MONITOR", color: "var(--ok)" };
  return null;
}

// 3D geometry pane placeholder — shown until a CAD / laser-scan model is onboarded.
function ThreeDPlaceholder(): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center text-center px-6 h-full"
         style={{ border: "1px dashed var(--hairline)", background: "var(--paper-2)", minHeight: 160 }}>
      <Box size={34} strokeWidth={1.1} style={{ color: "var(--ink-4)" }} />
      <div className="mt-3 font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".18em", color: "var(--ink-3)" }}>
        3D model not on file
      </div>
      <p className="mt-1.5 font-mono" style={{ fontSize: 10, color: "var(--ink-4)", maxWidth: 280, lineHeight: 1.55 }}>
        Onboard a CAD / laser-scan model to view the geometry and map CMLs onto it.
      </p>
    </div>
  );
}

// Exported for reuse by the guest landing view (docs/superpowers/specs/
// 2026-07-23-guest-access-design.md, PR 2) — a guest's read-only asset view
// renders the SAME CML table/detail the customer sees, never a rebuilt copy.
export function CmlIntegrityTable({ cmls, govCml, selId, onSelect, survey }: {
  cmls: CmlRow[]; govCml?: string; selId?: number; onSelect: (c: CmlRow) => void; survey?: string | null;
}): JSX.Element {
  // Sequential CML order (how the UTM grid is read down the asset); risk is
  // shown by the status dot + the governing "G" badge, not by reordering.
  const rows = useMemo(
    () => [...cmls].sort((a, b) => a.cml_no.localeCompare(b.cml_no, undefined, { numeric: true })),
    [cmls],
  );
  if (!cmls.length) return <div className="font-mono text-xs py-4" style={{ color: "var(--ink-3)" }}>No CMLs registered for this asset.</div>;
  return (
    <div className="border" style={{ borderColor: "var(--hairline)" }}>
      <div className="grid items-center gap-2 px-3 py-2 border-b font-mono uppercase"
           style={{ gridTemplateColumns: GRID, fontSize: 8.5, letterSpacing: ".1em", color: "var(--ink-4)", background: "var(--paper-2)", borderColor: "var(--hairline)" }}>
        <span>CML</span>
        <span className="text-right">MIN</span>
        <span className="text-right">t-MIN</span>
        <span className="text-right">MARGIN</span>
        <span className="text-right">CR</span>
        <span className="text-right">RL</span>
      </div>
      <div className="overflow-auto" style={{ maxHeight: 440 }}>
        {rows.map((c) => <CmlRowView key={c.cml_id} c={c} gov={c.cml_no === govCml}
                                     selected={c.cml_id === selId} onSelect={() => onSelect(c)} survey={survey} />)}
      </div>
    </div>
  );
}

function CmlRowView({ c, gov, selected, onSelect, survey }: { c: CmlRow; gov: boolean; selected: boolean; onSelect: () => void; survey?: string | null }): JSX.Element {
  // Resolve which survey to display: explicit selection, else the latest.
  const hist = c.history ?? [];
  const idx = survey ? hist.findIndex((h) => h.date === survey) : hist.length - 1;
  const curArr = (idx >= 0 ? hist[idx]?.pos : undefined) ?? (survey ? undefined : c.positions);
  const prevPosArr = idx > 0 ? hist[idx - 1]?.pos : undefined;
  const at = (label: string): number | null => {
    const p = curArr?.find((x) => x.pos === label);
    return p && p.mm > 0 ? p.mm : null;
  };
  const prevPos = (label: string): number | null => {
    const p = prevPosArr?.find((x) => x.pos === label);
    return p && p.mm > 0 ? p.mm : null;
  };
  // A 0/negative "reading" is a placeholder for no-data — never render it as a
  // thickness (0.00 mm reads as total wall loss). Show "–" instead.
  const rawMin = at("MIN") ?? (idx >= 0 ? hist[idx]?.mm ?? null : c.t_actual_mm);
  const minMm = rawMin != null && rawMin > 0 ? rawMin : null;
  const insuff = c.data_quality === "insufficient";
  // Status reflects the displayed survey's MIN vs t_min (so the row is self-consistent).
  const st: "crit" | "warn" | "ok" | "" =
    (c.t_min_mm && c.t_min_mm > 0 && minMm != null)
      ? (minMm <= c.t_min_mm ? "crit" : minMm < c.t_min_mm * 1.20 ? "warn" : "ok") : "";
  const dot = st ? ASSET_HEALTH[st].color : "#9CA3AF";
  const dqTip = dqTooltip(c.dq);
  return (
    <div role="button" onClick={onSelect} aria-label={`CML ${c.cml_no}`}
         className="grid items-center gap-2 px-3 py-1.5 border-b font-mono tabular-nums cursor-pointer transition-colors hover:bg-[rgba(var(--accent-rgb)_/_0.05)]"
         style={{ gridTemplateColumns: GRID, fontSize: 11, borderColor: "var(--hairline)",
                  background: selected ? "rgba(31,78,121,0.07)" : undefined,
                  borderLeft: `2px solid ${gov ? "var(--accent)" : selected ? "var(--ink-3)" : "transparent"}` }}>
      <span className="truncate flex items-center gap-1.5" style={{ color: "var(--ink-1)", fontWeight: gov || selected ? 700 : 400 }}>
        <span className="shrink-0 rounded-full" style={{ width: 7, height: 7, background: dot }} />
        {c.cml_no}
        {gov && <span className="font-mono uppercase shrink-0" style={{ fontSize: 7, letterSpacing: ".05em", color: "var(--accent-deep)", border: "1px solid var(--accent)", padding: "0 3px" }}>G</span>}
        {dqTip && (
          <span role="img" aria-label="ค่าที่น่าสงสัย" title={dqTip}
                className="shrink-0" style={{ fontSize: 10, lineHeight: 1, color: "var(--warn)" }}>
            ⚠
          </span>
        )}
      </span>
      <span className="text-right leading-tight">
        <span style={{ color: st === "crit" ? "var(--err)" : "var(--ink-1)", fontWeight: 700 }}>{minMm != null ? minMm.toFixed(2) : "–"}</span>
        {(() => { const pm = prevPos("MIN"); const d = (minMm != null && pm != null) ? minMm - pm : null;
          return d != null ? <span className="block" style={{ fontSize: 8, color: d > 0.05 ? "#b06d12" : "var(--ink-4)" }}>({d >= 0 ? "+" : "−"}{Math.abs(d).toFixed(2)})</span> : null; })()}
      </span>
      <span className="text-right" style={{ color: "var(--ink-3)" }}>{c.t_min_mm && c.t_min_mm > 0 ? c.t_min_mm.toFixed(2) : "–"}</span>
      {(() => {
        // Margin to the retirement limit — the number a client actually decides on.
        // Negative = wall already below t-min (breach). Same source as the studio.
        const margin = (minMm != null && c.t_min_mm && c.t_min_mm > 0) ? minMm - c.t_min_mm : null;
        const col = margin == null ? "var(--ink-4)"
          : margin < 0 ? "var(--err)" : margin < (c.t_min_mm as number) * 0.2 ? "#b06d12" : "var(--ink-2)";
        return <span className="text-right" style={{ fontWeight: 700, color: col }}>
          {margin != null ? `${margin >= 0 ? "+" : "−"}${Math.abs(margin).toFixed(2)}` : "–"}</span>;
      })()}
      <span className="text-right" style={{ color: "var(--ink-3)" }}>{c.governing_rate_mm_yr != null ? c.governing_rate_mm_yr.toFixed(3) : "–"}</span>
      <span className="text-right truncate" style={{ color: insuff ? "var(--ink-3)" : "var(--ink-2)", fontSize: insuff ? 9.5 : 11, fontStyle: insuff ? "italic" : "normal" }}>
        {c.remaining_life_years != null ? fmtYr(c.remaining_life_years) : insuff ? "insuff." : "–"}
      </span>
    </div>
  );
}

// ── GVI findings (free — visual inspection results) ──
const SEV: Record<string, { label: string; color: string }> = {
  crit: { label: "Critical", color: "var(--err)" }, warn: { label: "Warn", color: "var(--warn)" },
  watch: { label: "Watch", color: "#CA8A04" }, info: { label: "Info", color: "#5A6B82" },
  "": { label: "—", color: "#9CA3AF" },
};
/** A visible empty/loading/error note (dashed box) — absence is stated, not just blank. */
function EmptyNote({ children, tone }: { children: string; tone?: "error" }): JSX.Element {
  return (
    <div className="px-3 py-2.5 font-mono" style={{
      fontSize: 11, letterSpacing: ".03em",
      color: tone === "error" ? "var(--err)" : "var(--ink-3)",
      border: "1px dashed var(--hairline)", background: "var(--paper-2)",
    }}>{children}</div>
  );
}
// A row is a real DEFECT only if it carries an observation: actionable severity,
// a measurement, or a disposition. Bare info-level checklist categories (external
// coating, bolting, welds…) with no content are COVERAGE, not findings — counting
// them as findings overstates "4 issues found" when nothing was actually found.
function isDefect(f: AssetFinding): boolean {
  return f.severity === "watch" || f.severity === "warn" || f.severity === "crit"
    || f.measurement_mm != null
    || (!!f.disposition && f.disposition.trim() !== "");
}
function FindingsPanel({ assetId }: { assetId: number }): JSX.Element {
  const q = useQuery({ queryKey: ["findings", assetId], queryFn: () => apiClient.assetFindings(assetId), enabled: !!assetId });
  const all = q.data?.findings ?? [];
  const defects = all.filter(isDefect);
  const coverage = all.filter((f) => !isDefect(f));
  return (
    <div>
      <div className="font-mono uppercase mb-3" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>/ GVI FINDINGS · {q.isSuccess ? defects.length : "—"}</div>
      {q.isPending && <EmptyNote>Loading…</EmptyNote>}
      {q.isError && <EmptyNote tone="error">Couldn't load findings — please retry.</EmptyNote>}
      {/* Talk only about FINDINGS (verifiable). Don't claim whether a GVI inspection
          happened — that signal isn't trustworthy in this data. */}
      {q.isSuccess && defects.length === 0 && coverage.length === 0 && (
        <EmptyNote>No GVI findings on record for this asset.</EmptyNote>
      )}
      {defects.length > 0 && (
        <div className="border" style={{ borderColor: "var(--hairline)" }}>
          {defects.map((f) => {
            const s = SEV[f.severity] ?? SEV[""];
            return (
              <div key={f.id} className="px-3 py-2 flex items-center gap-3 border-b" style={{ borderColor: "var(--hairline)", borderLeft: `2px solid ${s.color}` }}>
                <span className="font-mono uppercase shrink-0" style={{ fontSize: 8.5, letterSpacing: ".06em", color: s.color, width: 52 }}>{s.label}</span>
                <span className="font-sans flex-1 truncate" style={{ fontSize: 12.5, color: "var(--ink-1)" }}>{f.description}</span>
                {f.defect_type && <span className="font-mono uppercase shrink-0" style={{ fontSize: 9, color: "var(--ink-4)" }}>{f.defect_type}</span>}
                {f.inspection_date && <span className="font-mono tabular-nums shrink-0" style={{ fontSize: 10, color: "var(--ink-4)" }}>{f.inspection_date.slice(0, 10)}</span>}
              </div>
            );
          })}
        </div>
      )}
      {q.isSuccess && coverage.length > 0 && <CoverageNote items={coverage} hadDefects={defects.length > 0} />}
    </div>
  );
}
function CoverageNote({ items, hadDefects }: { items: AssetFinding[]; hadDefects: boolean }): JSX.Element {
  // Info-level GVI checklist entries carried in the data (no measurement/disposition).
  // They add no per-asset signal — every asset gets the same generic categories — so
  // state the conclusion in one line consistent with the "· 0" header, and keep the
  // category list + provenance in the tooltip rather than contradicting the count.
  const areas = items.map((i) => i.description).filter(Boolean).join(" · ");
  return (
    <div className={hadDefects ? "mt-2" : ""}>
      <div className="px-3 py-2.5 font-mono" style={{
        fontSize: 11, letterSpacing: ".02em", lineHeight: 1.5,
        color: "var(--ink-3)", border: "1px dashed var(--hairline)", background: "var(--paper-2)",
      }}
        title={`GVI checklist on file (info-level only, no measurement or disposition): ${areas}. Carried in the inspection data — not an independently confirmed GVI walkdown.`}>
        <span style={{ color: "#3f7d4f" }}>No GVI defects recorded</span>
        {" · "}{items.length} checklist {items.length === 1 ? "entry" : "entries"} on file, info-level only
      </div>
    </div>
  );
}

// ── Reference photos (free — lazy thumbnail grid) ──
function PhotosPanel({ assetId }: { assetId: number }): JSX.Element {
  const q = useQuery({ queryKey: ["asset-photos", assetId], queryFn: () => apiClient.assetPhotos(assetId), enabled: !!assetId });
  const ph = q.data ?? [];
  const [zoom, setZoom] = useState<{ url: string; date?: string } | null>(null);
  return (
    <div>
      <div className="font-mono uppercase mb-3" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>/ PHOTOS · {q.isSuccess ? ph.length : "—"}</div>
      {q.isPending && <EmptyNote>Loading…</EmptyNote>}
      {q.isError && <EmptyNote tone="error">Couldn't load photos — please retry.</EmptyNote>}
      {q.isSuccess && ph.length === 0 && <EmptyNote>No reference photos on record for this asset.</EmptyNote>}
      <div className="flex gap-3 flex-wrap">
        {ph.map((p) => <Thumb key={String(p.idx)} assetId={assetId} idx={p.idx} date={p.date}
                              onOpen={(url) => setZoom({ url, date: p.date })} />)}
      </div>
      {zoom && <Lightbox url={zoom.url} date={zoom.date} onClose={() => setZoom(null)} />}
    </div>
  );
}
function Thumb({ assetId, idx, date, onOpen }: { assetId: number; idx: number | string; date?: string; onOpen: (url: string) => void }): JSX.Element {
  const q = useQuery({ queryKey: ["photo", assetId, idx], queryFn: () => apiClient.assetPhotoUrl(assetId, idx), enabled: !!assetId, staleTime: Infinity });
  return (
    <button type="button" onClick={() => q.data && onOpen(q.data)} disabled={!q.data}
            className="block text-left group" style={{ width: 196, cursor: q.data ? "zoom-in" : "default" }}>
      <div className="flex items-center justify-center" style={{ width: 196, height: 147, border: "1px solid var(--hairline)", background: "var(--paper-2)", overflow: "hidden" }}>
        {q.data ? <img src={q.data} alt={String(date ?? "photo")} className="group-hover:opacity-90 transition-opacity"
                       style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <span className="font-mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>…</span>}
      </div>
      {date && <div className="font-mono tabular-nums" style={{ fontSize: 9.5, color: "var(--ink-4)", marginTop: 3 }}>{String(date).slice(0, 10)}</div>}
    </button>
  );
}
// Full-screen photo viewer — inspectors need defect-level detail, not just a chip.
function Lightbox({ url, date, onClose }: { url: string; date?: string; onClose: () => void }): JSX.Element {
  return (
    <div onClick={onClose} className="fixed inset-0 flex flex-col items-center justify-center"
         style={{ background: "rgba(8,12,18,.88)", zIndex: 60, padding: 32 }}>
      <img src={url} alt={String(date ?? "photo")} onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: "92vw", maxHeight: "86vh", objectFit: "contain", border: "1px solid rgba(255,255,255,.14)", cursor: "default" }} />
      <div className="font-mono mt-3" style={{ fontSize: 11, letterSpacing: ".06em", color: "#cbd5e1" }}>
        {date ? `${String(date).slice(0, 10)} · ` : ""}CLICK ANYWHERE TO CLOSE
      </div>
    </div>
  );
}

// ── Premium: action recommendation (gated behind the recommendation feature) ──
// RemainingLifeProjection now lives in @/components/RemainingLifeProjection
// (workbench roadmap C1 — shared with the staff Desk's what-if calculator).

// Structured expert assessment (from the review studio) rendered for the customer.
function AssessmentView({ a }: { a: AssetAssessment }): JSX.Element {
  const PRI: Record<string, string> = { urgent: "var(--err)", priority: "var(--warn)", routine: "#5A6B82" };
  const sec = (label: string, body: string) => body ? (
    <div>
      <div className="font-mono uppercase mb-1" style={{ fontSize: 8.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>{label}</div>
      <div className="font-sans" style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--ink-2)" }}>{body}</div>
    </div>
  ) : null;
  // Field order mirrors the staff Review Studio's Analysis Report top-to-bottom
  // (Summary → DM → Interval basis → Corrosion-rate basis → FFS → Action plan →
  // Confidence) so an engineer can lay the client view next to the signed studio
  // view and QA/QC them as a straight visual diff. Staff-only fields (cert ref,
  // internal notes) are intentionally NOT surfaced — client lens.
  return (
    <div className="mt-3 pt-3 space-y-3" style={{ borderTop: "1px solid var(--hairline)" }}>
      {sec("Engineer summary", a.summary)}
      {sec("Damage mechanism (API 571)", a.damage_mechanisms)}
      {sec("Inspection interval basis", a.interval_basis)}
      {sec("Corrosion-rate basis", a.corrosion_basis)}
      {a.ffs?.required && sec("Fitness-for-service (API 579)", a.ffs.note || "Required.")}
      {a.actions && a.actions.length > 0 && (
        <div>
          <div className="font-mono uppercase mb-1.5" style={{ fontSize: 8.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>Action plan</div>
          <div className="space-y-1.5">
            {a.actions.map((ac, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="font-mono uppercase shrink-0 px-1.5 mt-px" style={{ fontSize: 8, letterSpacing: ".06em", color: "#fff", background: PRI[ac.priority] || PRI.routine }}>{ac.priority}</span>
                <span className="font-sans flex-1" style={{ fontSize: 12.5, lineHeight: 1.45, color: "var(--ink-2)" }}>{ac.action}{ac.due ? ` — ${ac.due}` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {sec("Assessment confidence", a.confidence)}
    </div>
  );
}

const DISPOSITION_LABEL: Record<string, string> = {
  continue: "Continue in service", monitor: "Monitor", inspect: "Inspect — establish baseline",
  ffs: "Fitness-for-Service required", repair: "Repair / plan replacement",
  rerate: "Re-rate", retire: "Retire — take out of service",
};
const DISPOSITION_COLOR: Record<string, string> = {
  ffs: "var(--err)", repair: "var(--err)", retire: "var(--err)",
  monitor: "var(--warn)", inspect: "var(--warn)", rerate: "var(--warn)", continue: "var(--ok)",
};
const TIER_LABEL = ["no baseline data", "single survey", "trended"];
const ACT_PRI: Record<string, string> = { urgent: "var(--err)", priority: "var(--warn)", routine: "#5A6B82" };

// The tier-gated engine recommendation (decision-first: disposition + why +
// evidence quality + actions). Honesty is gated server-side by data_tier.
function RecommendationView({ rec }: { rec: EngineRecommendation }): JSX.Element {
  const color = DISPOSITION_COLOR[rec.disposition] ?? "var(--ink-2)";
  const c = rec.confidence;
  return (
    <div className="mt-1">
      <div className="font-sans font-bold" style={{ fontSize: 16, color }}>{DISPOSITION_LABEL[rec.disposition] ?? rec.disposition}</div>
      {rec.basis.reason && (
        <p className="font-sans mt-0.5" style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--ink-2)" }}>
          {rec.basis.reason}{rec.basis.standard_ref ? ` · ${rec.basis.standard_ref}` : ""}
        </p>
      )}
      <div className="mt-2 px-2.5 py-1.5" style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
        <div className="font-mono uppercase" style={{ fontSize: 8, letterSpacing: ".14em", color: "var(--ink-4)" }}>Evidence quality · {c.overall}</div>
        <div className="font-mono mt-0.5" style={{ fontSize: 10.5, color: "var(--ink-3)" }}>
          tier {rec.data_tier} ({TIER_LABEL[rec.data_tier] ?? "—"}) · {c.cmls_trendable}/{c.n_cmls} CML trended{c.n_below_tmin > 0 ? ` · ${c.n_below_tmin} below t-min` : ""}
        </div>
        {rec.timing.interval_years == null && rec.data_tier < 2 && (
          <div className="font-mono mt-0.5" style={{ fontSize: 10, color: "var(--ink-4)" }}>Remaining life / interval not computable at this data tier.</div>
        )}
      </div>
      {rec.actions.length > 0 && (
        <div className="mt-2 space-y-1">
          {rec.actions.map((a, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="font-mono uppercase shrink-0 px-1.5 mt-px" style={{ fontSize: 8, letterSpacing: ".06em", color: "#fff", background: ACT_PRI[a.priority] ?? ACT_PRI.routine }}>{a.priority}</span>
              <span className="font-sans" style={{ fontSize: 12.5, lineHeight: 1.45, color: "var(--ink-2)" }}>{a.action}{a.due ? ` — ${a.due}` : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Human-expert review overlay — the premium layer that turns the automated
// analysis into an attributable, signed assessment. Customers REQUEST; staff VERIFY.
export function ReviewLayer({ assetId }: { assetId: number }): JSX.Element | null {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["review", assetId], queryFn: () => apiClient.assetReview(assetId), enabled: !!assetId });
  const rv = q.data;
  const user = getCachedUser();
  const isStaff = !!user && (STAFF_ROLES as readonly string[]).includes(user.role);
  const [busy, setBusy] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  if (!rv) return null;
  const verified = rv.status === "verified";
  const pending = rv.status === "requested" || rv.status === "in_review";
  const d = (x: string | null) => (x ? x.slice(0, 10) : "");
  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    try { await fn(); await qc.invalidateQueries({ queryKey: ["review", assetId] }); }
    finally { setBusy(false); }
  }
  const accent = verified ? "var(--ok)" : pending ? "var(--warn)" : "var(--ink-4)";
  const rec = rv.recommendation;
  return (
    <div className="space-y-2">
      {rv.stale && (
        <div className="px-3 py-2 font-mono" style={{ fontSize: 11, color: "var(--warn)", border: "1px solid #D9770644", background: "#D977060a" }}>
          ⚠ Analysis changed since this was verified — re-review recommended.
        </div>
      )}
      <div className="px-4 py-3 flex items-start gap-3" style={{ border: "1px solid var(--hairline)", borderLeft: `3px solid ${accent}`, background: "var(--paper-2)" }}>
        {verified ? <ShieldCheck size={18} style={{ color: accent, marginTop: 1 }} />
          : pending ? <Clock size={18} style={{ color: accent, marginTop: 1 }} />
          : <Bot size={18} style={{ color: accent, marginTop: 1 }} />}
        <div className="flex-1 min-w-0">
          {verified ? (
            <>
              <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".14em", color: accent, fontWeight: 700 }}>Engineer-verified</div>
              <div className="font-sans mt-0.5" style={{ fontSize: 13, color: "var(--ink-1)", fontWeight: 600 }}>{rv.verdict || "Reviewed"}</div>
              {rv.expert_notes && <p className="font-sans mt-1" style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--ink-2)" }}>{rv.expert_notes}</p>}
              <div className="font-mono mt-1.5" style={{ fontSize: 10, color: "var(--ink-4)" }}>{rv.reviewer_name}{rv.reviewer_cert ? ` · ${rv.reviewer_cert}` : ""}{rv.reviewed_at ? ` · ${d(rv.reviewed_at)}` : ""}</div>
              {/* QA/QC traceability: the live engine output and the engineer-signed
                  assessment are in sync (drift would set rv.stale → the amber banner
                  above). Lets a reviewer confirm the client sees exactly what was signed. */}
              {!rv.stale && (
                <div className="font-mono mt-1.5 flex items-center gap-1" style={{ fontSize: 10, letterSpacing: ".02em", color: "var(--ok)" }}>
                  <span aria-hidden>✓</span> Current analysis matches this signed assessment
                </div>
              )}
              {rv.assessment && <AssessmentView a={rv.assessment} />}
            </>
          ) : (
            <>
              <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".14em", color: accent, fontWeight: 700 }}>
                {pending ? (rv.status === "in_review" ? "In review · preliminary" : "Review requested · preliminary") : "Preliminary · engine analysis"}
              </div>
              {rec && <RecommendationView rec={rec} />}
              <p className="font-mono mt-2" style={{ fontSize: 10, color: "var(--ink-4)" }}>
                {pending
                  ? (rv.status === "in_review" ? `Under review by ${rv.reviewer_name || "an engineer"} — sign-off pending.` : `Requested by ${rv.requested_by || "—"}${rv.requested_at ? ` · ${d(rv.requested_at)}` : ""} — awaiting an engineer.`)
                  : "Not yet reviewed by an engineer. Request a sign-off for a verified, attributable assessment."}
              </p>
              {!verified && !pending && !isStaff && (
                <button onClick={() => act(() => apiClient.requestReview(assetId))} disabled={busy}
                        className="mt-2 px-3 py-1.5 font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".1em", color: "#fff", background: "var(--accent)", cursor: "pointer" }}>
                  {busy ? "…" : "Request engineer review"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {isStaff && user?.can_sign_off && (
        <div className="flex items-center gap-2">
          {rv.status === "requested" && (
            <button onClick={() => act(() => apiClient.claimReview(assetId))} disabled={busy}
                    className="px-3 py-1.5 font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--ink-1)", border: "1px solid var(--hairline)", cursor: "pointer" }}>
              {busy ? "…" : "Claim"}
            </button>
          )}
          <button onClick={() => setVerifyOpen(true)}
                  className="px-3 py-1.5 font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".1em", color: "#fff", background: "var(--ok)", cursor: "pointer" }}>
            {verified ? "Re-verify" : "Verify / sign off"}
          </button>
        </div>
      )}
      {isStaff && !user?.can_sign_off && (
        <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--ink-4)" }}>
          Level III sign-off authorization required
        </div>
      )}
      {verifyOpen && (
        <VerifyForm assetId={assetId} reverify={verified}
                    onClose={() => setVerifyOpen(false)}
                    onVerified={async () => { await qc.invalidateQueries({ queryKey: ["review", assetId] }); await qc.invalidateQueries({ queryKey: ["pending-reviews"] }); setVerifyOpen(false); }} />
      )}
    </div>
  );
}

// Premium decision-support: verdict + key metrics + risk drivers + a concrete
// action plan + assessment confidence — all from the deterministic corrosion
// engine (no fabricated RBI inputs).
function RecommendationPanel({ assetId, corrosion }: { assetId: number; corrosion?: CorrosionData }): JSX.Element {
  const rollup = corrosion?.rollup;
  const cmls = corrosion?.cmls ?? [];
  const drivers = [...cmls].filter((c) => c.remaining_life_years != null)
    .sort((a, b) => (a.remaining_life_years as number) - (b.remaining_life_years as number)).slice(0, 5);
  const assessable = cmls.filter((c) => c.remaining_life_years != null || c.governing_rate_mm_yr != null).length;
  const govCml = rollup?.governing ? cmls.find((c) => c.cml_no === rollup.governing!.cml_no) : undefined;
  const metric = (l: string, v: string, c?: string) => (
    <div className="px-3 py-2 border-r last:border-r-0" style={{ borderColor: "var(--hairline)" }}>
      <div className="font-mono uppercase" style={{ fontSize: 8, letterSpacing: ".14em", color: "var(--ink-4)" }}>{l}</div>
      <div className="font-mono tabular-nums" style={{ fontSize: 14, fontWeight: 700, color: c ?? "var(--ink-1)" }}>{v}</div>
    </div>
  );
  return (
    <div className="space-y-4" style={{ maxWidth: 760 }}>
      <ReviewLayer assetId={assetId} />
      {govCml && (
        <div>
          <div className="font-mono uppercase mb-2" style={{ fontSize: 9, letterSpacing: ".18em", color: "var(--ink-4)" }}>/ Remaining-life projection — governing CML {govCml.cml_no}</div>
          <RemainingLifeProjection c={govCml} nextDue={rollup?.next_inspection_due} />
          <div className="font-mono mt-1" style={{ fontSize: 8.5, color: "var(--ink-4)", letterSpacing: ".03em" }}>
            Solid = measured · dashed = projected at the governing corrosion rate to t-min (API 510/570). Not a stamped assessment.
          </div>
        </div>
      )}

      {rollup && (
        <div className="grid grid-cols-4 border" style={{ borderColor: "var(--hairline)" }}>
          {metric("GOVERNING CML", rollup.governing?.cml_no ?? [...cmls].filter((c) => c.t_min_mm != null && c.t_actual_mm != null && (c.t_actual_mm as number) < (c.t_min_mm as number)).sort((a, b) => ((a.t_actual_mm as number) - (a.t_min_mm as number)) - ((b.t_actual_mm as number) - (b.t_min_mm as number)))[0]?.cml_no ?? "—")}
          {metric("REMAINING LIFE", rollup.min_remaining_life_years != null ? `${rollup.min_remaining_life_years.toFixed(1)} yr` : rollup.no_measurable_loss ? "no loss" : "—")}
          {metric("CORROSION RATE", rollup.governing?.governing_rate_mm_yr != null ? `${rollup.governing.governing_rate_mm_yr.toFixed(3)} mm/yr` : "—")}
          {metric("NEXT DUE", rollup.overdue ? "OVERDUE" : (rollup.next_inspection_due ?? "—"), rollup.overdue ? "var(--err)" : undefined)}
        </div>
      )}

      {drivers.length > 0 && (
        <div>
          <div className="font-mono uppercase mb-2" style={{ fontSize: 9, letterSpacing: ".18em", color: "var(--ink-4)" }}>/ Risk drivers — shortest remaining life</div>
          <div className="border divide-y" style={{ borderColor: "var(--hairline)" }}>
            {drivers.map((c) => (
              <div key={c.cml_id} className="px-3 py-2 flex items-center gap-4 font-mono tabular-nums" style={{ fontSize: 11 }}>
                <span style={{ color: "var(--ink-1)", fontWeight: 600, minWidth: 52 }}>CML {c.cml_no}</span>
                <span style={{ color: "var(--ink-2)" }}>RL {(c.remaining_life_years as number) >= 100 ? ">100" : (c.remaining_life_years as number).toFixed(1)} yr</span>
                <span style={{ color: "var(--ink-3)" }}>CR {c.governing_rate_mm_yr != null ? c.governing_rate_mm_yr.toFixed(3) : "—"} mm/yr</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {corrosion && (
        <p className="font-mono" style={{ fontSize: 9.5, color: "var(--ink-4)", letterSpacing: ".03em" }}>
          Assessed from {assessable} of {cmls.length} CMLs with corrosion-rate data{cmls.length - assessable > 0 ? ` · ${cmls.length - assessable} insufficient` : ""}.
        </p>
      )}
      <p className="font-mono" style={{ fontSize: 9, color: "var(--ink-4)", letterSpacing: ".04em" }}>
        Advisory only — not a substitute for the stamped engineering assessment.
      </p>
    </div>
  );
}

// ── Selected-CML detail pane: trend + stats + readings ──
// Exported — same reuse reason as CmlIntegrityTable above.
export function CmlDetail({ c }: { c: CmlRow }): JSX.Element {
  const st = cmlStatus(c);
  const meta = ASSET_HEALTH[st];
  const margin = (c.t_actual_mm != null && c.t_min_mm && c.t_min_mm > 0) ? c.t_actual_mm - c.t_min_mm : null;
  const flags = [c.flags?.cui && "CUI", c.flags?.dead_leg && "Dead leg", c.flags?.injection && "Injection"].filter(Boolean) as string[];
  const rev = [...c.history].filter((h) => h.mm > 0).reverse();
  const stat = (label: string, value: string, color?: string) => (
    <div>
      <div className="font-mono uppercase" style={{ fontSize: 8, letterSpacing: ".14em", color: "var(--ink-4)" }}>{label}</div>
      <div className="font-mono tabular-nums" style={{ fontSize: 13, color: color ?? "var(--ink-1)", fontWeight: 600 }}>{value}</div>
    </div>
  );
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-2.5">
        <span className="font-mono" style={{ fontSize: 14, color: "var(--ink-1)", fontWeight: 700 }}>
          {c.cml_no}<span className="ml-2 uppercase" style={{ fontSize: 9, color: "var(--ink-4)" }}>{c.cml_type}</span>
        </span>
        <span className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".08em", color: st ? meta.color : "var(--ink-4)" }}>{st ? meta.label : "spec ?"}</span>
      </div>
      <RemainingLifeProjection c={c} />
      <div className="grid grid-cols-3 gap-y-3 gap-x-2 mt-3 pt-3 border-t" style={{ borderColor: "var(--hairline)" }}>
        {stat("t-now", fmtMm(c.t_actual_mm) + " mm")}
        {stat("t-min", c.t_min_mm && c.t_min_mm > 0 ? fmtMm(c.t_min_mm) + " mm" : "—", c.t_min_mm && c.t_min_mm > 0 ? undefined : "var(--ink-4)")}
        {stat("margin", margin != null ? (margin >= 0 ? "+" : "") + fmtMm(margin) + " mm" : "—", margin != null && margin < 0 ? "var(--err)" : undefined)}
        {stat("CR", fmtRate(c.governing_rate_mm_yr) + " mm/yr")}
        {stat("rem. life", c.remaining_life_years != null ? fmtYr(c.remaining_life_years) + " yr" : "—", c.remaining_life_years == null ? "var(--ink-4)" : undefined)}
        {stat("data", c.data_quality, "var(--ink-3)")}
      </div>
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {flags.map((f) => <span key={f} className="font-mono uppercase px-1.5 py-px" style={{ fontSize: 8, letterSpacing: ".1em", color: "#b06d12", background: "rgba(217,119,6,0.12)", border: "1px solid rgba(217,119,6,0.3)" }}>{f}</span>)}
        </div>
      )}
      {rev.length > 0 && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--hairline)" }}>
          <div className="font-mono uppercase mb-1.5" style={{ fontSize: 8, letterSpacing: ".14em", color: "var(--ink-4)" }}>Readings · {rev.length}</div>
          <div className="max-h-[120px] overflow-auto">
            {rev.map((h, i) => (
              <div key={i} className="flex items-center justify-between py-0.5 font-mono tabular-nums" style={{ fontSize: 10.5 }}>
                <span style={{ color: "var(--ink-3)" }}>{h.date.slice(0, 10)}</span>
                <span style={{ color: "var(--ink-1)" }}>{fmtMm(h.mm)} mm</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// HistoryRow (inspection documents list)
// ─────────────────────────────────────────────────────────────────────
// Per-inspection outcome from its result kind (thickness → min mm / breaches;
// visual → defects / checklist). Honest: states only what the data carries.
function outcomeOf(it: InspectionHistoryItem): { label: string; color: string } {
  if (it.result_kind === "thickness") {
    if ((it.n_below_tmin ?? 0) > 0) return { label: `${it.n_below_tmin} CML below t-min`, color: "var(--err)" };
    if ((it.reading_count ?? 0) > 0) return { label: `min ${(it.min_thickness_mm ?? 0).toFixed(2)} mm · ${it.reading_count} readings · no breach`, color: "var(--ok)" };
    return { label: "no readings captured", color: "var(--ink-3)" };
  }
  if (it.result_kind === "visual") {
    if ((it.defect_count ?? 0) > 0) return { label: `${it.defect_count} defect${it.defect_count === 1 ? "" : "s"}`, color: "var(--warn)" };
    if ((it.checklist_count ?? 0) > 0) return { label: `no defects · ${it.checklist_count} checklist (info)`, color: "var(--ink-3)" };
    return { label: "no findings", color: "var(--ink-3)" };
  }
  return { label: "—", color: "var(--ink-3)" };
}

function HistoryRow({ it }: { it: InspectionHistoryItem }): JSX.Element {
  const [st, setSt] = useState<"idle" | "loading" | "error">("idle");
  const out = outcomeOf(it);
  const date = it.inspection_date ? it.inspection_date.slice(0, 10) : "—";
  async function download(): Promise<void> {
    if (st === "loading") return;
    setSt("loading");
    try {
      const { blob, filename } = await apiClient.inspectionRawExcel(it.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename || `${it.doc_no || `INSP-${it.id}`}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setSt("idle");
    } catch (err) {
      console.error("Download failed", err);
      setSt("error");
    }
  }
  return (
    <button type="button" onClick={download} title="Download raw data (source workbook)"
            className="w-full text-left px-4 py-3 flex items-start gap-3 group hover:bg-[rgba(31,78,121,0.04)] transition-colors">
      <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5"
           style={{ background: "rgba(var(--accent-rgb) / 0.08)", border: "1px solid rgba(var(--accent-rgb) / 0.3)" }}>
        <FileText size={14} style={{ color: "var(--accent)" }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono" style={{ fontSize: 12.5, color: "var(--ink-1)", fontWeight: 600 }}>{it.doc_no || `INSP-${it.id}`}</span>
          <span className="font-mono uppercase px-1.5 shrink-0" style={{ fontSize: 8.5, letterSpacing: ".1em", color: "var(--accent-deep)", border: "1px solid var(--hairline)" }}>{it.technique}</span>
          <span className="font-mono uppercase shrink-0" style={{ fontSize: 8.5, letterSpacing: ".1em", color: "var(--ink-4)" }}>{it.status}</span>
        </div>
        <div className="font-mono mt-1 flex items-center gap-2" style={{ fontSize: 10, color: "var(--ink-3)" }}>
          <Calendar size={9} /><span className="tabular-nums">{date}</span>
          {it.inspector_name && (<><span>·</span><UserIcon size={9} /><span className="truncate">{it.inspector_name}</span></>)}
        </div>
        <div className="font-mono mt-1" style={{ fontSize: 11, color: out.color, fontWeight: 600 }}>{out.label}</div>
      </div>
      <span className="shrink-0 mt-1 flex items-center gap-1 font-mono uppercase"
            style={{ fontSize: 8.5, letterSpacing: ".12em", color: st === "error" ? "var(--err)" : "var(--accent-deep)" }}>
        {st === "loading" ? <Loader2 size={12} className="animate-spin" />
          : st === "error" ? <span className="normal-case" style={{ letterSpacing: 0 }}>no raw source</span>
          : <><span className="opacity-0 group-hover:opacity-100 transition-opacity">RAW</span><Download size={12} /></>}
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
function KpiCell({ label, value, valueColor, tip }: { label: string; value: number | string; valueColor?: string; tip?: string }): JSX.Element {
  return (
    <div className={"px-4 py-2 min-w-[110px] border-r last:border-r-0" + (tip ? " cursor-help" : "")}
         style={{ borderColor: "var(--hairline)" }} title={tip}>
      <div className="font-mono uppercase inline-flex items-center gap-1"
           style={{ fontSize: 9.5, letterSpacing: ".22em", color: "var(--ink-4)" }}>
        {label}
        {tip && <Info size={9} strokeWidth={2} className="opacity-40 mb-px" />}
      </div>
      <div className="font-sans font-bold tabular-nums leading-tight"
           style={{ fontSize: 20, color: valueColor ?? "var(--ink-1)" }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function SpecCell({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="px-4 py-3 border-r last:border-r-0"
         style={{ borderColor: "var(--hairline)" }}>
      <div className="font-mono uppercase"
           style={{ fontSize: 9.5, letterSpacing: ".22em", color: "var(--ink-4)" }}>
        {label}
      </div>
      <div className="font-mono mt-0.5 truncate"
           style={{ fontSize: 13, color: "var(--ink-1)", fontWeight: 500 }}>
        {value}
      </div>
    </div>
  );
}

function CenterStatus({
  children, tone,
}: { children: React.ReactNode; tone?: "err" }): JSX.Element {
  return (
    <div className="absolute inset-0 flex items-center justify-center" style={{ background: "var(--paper)" }}>
      <div className="font-mono uppercase"
           style={{
             fontSize: 12, letterSpacing: ".22em",
             color: tone === "err" ? "var(--err)" : "var(--ink-3)",
           }}>
        {children}
      </div>
    </div>
  );
}
